import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./TimeTrialPopup.css";
import { useGetActiveTimeTrailsQuery } from "../../redux-toolkit/features/time-trails/timeTrailsApiSlice";
import time_trails_icon from "../../assets/time_trails_icon.svg";

export const TimeTrialPopup = ({ isVisible, onClose }) => {
  const navigate = useNavigate();
  const { data: activeTimeTrials } = useGetActiveTimeTrailsQuery();

  // Get the first active time trial for display
  const activeTimeTrial = activeTimeTrials?.data;

  const [timeRemaining, setTimeRemaining] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (activeTimeTrial) {
      const now = new Date();
      const endDate = new Date(activeTimeTrial.endDate);
      const startDate = new Date(activeTimeTrial.startDate);

      // Calculate total duration and remaining time
      const totalDuration = endDate.getTime() - startDate.getTime();
      const remainingTime = endDate.getTime() - now.getTime();

      // Calculate progress percentage
      const elapsedTime = now.getTime() - startDate.getTime();
      const progressPercent = Math.max(
        0,
        Math.min(100, (elapsedTime / totalDuration) * 100)
      );

      // Set initial values
      setProgress(progressPercent);
      setTimeRemaining(Math.max(0, Math.floor(remainingTime / 1000))); // Convert to seconds

      // Start countdown timer
      if (remainingTime > 0) {
        const timer = setInterval(() => {
          setTimeRemaining((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });

          // Update progress
          const currentTime = new Date();
          const newElapsedTime = currentTime.getTime() - startDate.getTime();
          const newProgressPercent = Math.max(
            0,
            Math.min(100, (newElapsedTime / totalDuration) * 100)
          );
          setProgress(newProgressPercent);
        }, 1000);

        return () => clearInterval(timer);
      }
    }
  }, [activeTimeTrial]);

  const formatTime = (seconds) => {
    if (seconds <= 0) return "00:00";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      // Show hours:minutes:seconds format (e.g., 32:12:50)
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
    } else {
      // Show minutes:seconds format (e.g., 12:50 or 00:50)
      return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
        .toString()
        .padStart(2, "0")}`;
    }
  };

  // Don't show popup if no active time trials
  if (!activeTimeTrial || !Object.keys(activeTimeTrial)?.length) return null;

  // Handle click to navigate to time trail details
  const handleClick = () => {
    if (activeTimeTrial?._id) {
      navigate(`/quest/time-trails/?task=${activeTimeTrial._id}`);
      // Close the popup if onClose is provided
      if (onClose) {
        onClose();
      }
    }
  };

  // Don't show if time trial has expired
  const now = new Date();
  const endDate = new Date(activeTimeTrial?.endDate);
  if (now > endDate) return null;

  return (
    <div className="time-trial-toast" onClick={handleClick}>
      <div className="time-trial-toast__card">
        <div className="time-trial-toast__content">
          <div className="time-trial-toast__icon">
            <img src={time_trails_icon} alt="Time Trails" />
          </div>

          <div className="time-trial-toast__text">
            <h3
              className="time-trial-toast__title"
              style={{ textAlign: "left" }}
            >
              Active Time Trial!
            </h3>
            <p className="time-trial-toast__description">
              {activeTimeTrial?.task ||
                "Complete this time trial to earn points!"}
            </p>
          </div>
        </div>

        <div className="time-trial-toast__progress">
          <div className="time-trial-toast__progress-bar">
            <div
              className="time-trial-toast__progress-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <span className="time-trial-toast__time">
            {formatTime(timeRemaining)}
          </span>
        </div>
      </div>
    </div>
  );
};
